import { parse } from "graphql";
import { SchemaClient } from "../../../common/GraphqlClient";
import {
  activeFilter,
  DynamoDbOperations,
  DynamoDbTables,
  Source,
} from "../../../common/Source";
import { EMPTY_VALUE } from "../../../Schema";



export type LinkedInstance = {
  linkedInstance: string;
  linkedInstanceDisplay: string;
  secondaryIdMapping: string;
  disableManualLinking?: boolean;
}

export const linkedInstancesQuery: Source<LinkedInstance[]> = {
  graphql: parse(`
    query listSupportedLinkedInstances($nextToken: String) {
      listSupportedLinkedInstances(
        limit: 10000,
        filter: { isActive: { eq: true } }
        nextToken: $nextToken
      ) {
        nextToken
        items {
          linkedInstance
          linkedInstanceDisplay
          secondaryIdMapping
          disableManualLinking
        }
      }
    }
  `),
  dynamodb: {
    // A scan, and correctly so: the table is keyed by linkedInstance with no sort key and
    // no secondary index, so there is no partition to narrow to. It holds ten rows.
    operation: DynamoDbOperations.SCAN,
    table: DynamoDbTables.LINKED_INSTANCES,
    filter: activeFilter,
  },
};

/**
 * Whether a linked instance names this field as one of its secondary identifiers.
 *
 * `secondaryIdMapping` is a `;`-separated list of field names, and it is the master data's own
 * statement of which columns identify an equipment on that instance. Reading it is what lets a
 * new linked instance be configured without a code change.
 *
 * Answers `undefined` rather than `false` when the instance declares no mapping at all, so a
 * caller can fall back to whatever it did before instead of dropping the requirement — an
 * instance with an empty mapping is a gap in the master data, not a statement that nothing is
 * required.
 *
 * @param linkedInstance - The row's linked instance, in schema space.
 * @param fieldName - The field to look for.
 * @returns Whether the mapping names the field, or `undefined` when there is no mapping.
 */
export const isSecondaryIdField = (
  linkedInstance: unknown,
  fieldName: string
): boolean | undefined => {
  const mapping = (linkedInstance as LinkedInstance | undefined)?.secondaryIdMapping;
  if (!mapping) {
    return undefined;
  }
  return mapping.split(";").map((token) => token.trim()).includes(fieldName);
};

export const getLinkedInstances = async (client: SchemaClient): Promise<LinkedInstance[] | typeof EMPTY_VALUE> => {
  return await client.queryList(linkedInstancesQuery);
};

export const getAndProcessLinkedInstances = async (client: SchemaClient) => {
  const linkedInstances = await getLinkedInstances(client);
  return linkedInstances?.map((linkedInstance: LinkedInstance) => ({
      linkedInstanceDisplay: linkedInstance.linkedInstanceDisplay,
      linkedInstance: linkedInstance.linkedInstance,
      secondaryIdMapping: linkedInstance.secondaryIdMapping,
      disableManualLinking: linkedInstance.disableManualLinking,
    })) ?? [];
  };